import React, { useState } from "react"
import { Redirect } from "react-router-dom"
import { Mail, Ticket, ArrowRight, ShieldCheck } from "lucide-react"
import { db } from "../../../utils/firebase"

/**
 * Assets
 */
import Logo from "../../../assets/images/golf-holidays-direct-logo.png"
import BackgroundImage from "../../../assets/images/login-background.jpeg"

import styles from "./login.module.scss"

/**
 * Client-facing login for the "Manage My Booking" portal.
 *
 * Auth model is unchanged from the original: a two-factor lookup of the booking
 * reference + the lead client's email against Firestore. There is no password
 * and no session — on a successful match we redirect to /itinerary/{bookingId},
 * and the booking document ID becomes the access token in the URL from then on.
 *
 * This file is a visual rebuild only (Phase 1 of the portal redesign); the
 * lookup logic below is intentionally identical to the legacy implementation.
 */
function ClientLogin() {
	const [errors, setErrors] = useState({})
	const [email, setEmail] = useState("")
	const [reference, setReference] = useState("")
	const [fetching, setFetching] = useState(false)
	const [bookingID, setBookingID] = useState("")

	/**
	 * Check the booking reference and client email against our records to see
	 * whether we should redirect them through to their booking.
	 */
	const checkBookingDetails = async () => {
		setFetching(true)
		setErrors({})

		/**
		 * First attempt to find a booking with the reference that was passed in.
		 * References can be stored as numbers or strings, so we try the numeric
		 * form first and fall back to a string match.
		 */
		const booking = await db
			.collection("bookings")
			.where("reference", "==", isNaN(Number(reference)) ? reference : Number(reference))
			.limit(1)
			.get()
			.then((bookingDocs) => {
				if (bookingDocs.size > 0) {
					const { client } = bookingDocs.docs[0].data()
					return { id: bookingDocs.docs[0].id, client }
				} else {
					// Try again with the reference as a string
					return db
						.collection("bookings")
						.where("reference", "==", reference)
						.limit(1)
						.get()
						.then((bookingDocs) => {
							if (bookingDocs.size > 0) {
								const { client } = bookingDocs.docs[0].data()
								return { id: bookingDocs.docs[0].id, client }
							} else {
								setErrors({ global: "Reference not found, please check and try again" })
								setFetching(false)
								return false
							}
						})
				}
			})

		/**
		 * If we found a booking, confirm the entered email matches the lead
		 * client's email before letting them through.
		 */
		if (booking) {
			await db
				.doc(`clients/${booking.client}`)
				.get()
				.then((clientDoc) => {
					if (clientDoc.exists && clientDoc.data().email.trim() === email.trim()) {
						setBookingID(booking.id)
					} else {
						setErrors({
							global: "Email not recognised on this booking, please contact your lead booker",
						})
						setFetching(false)
						return false
					}
				})
		}
	}

	/**
	 * Submit on form submission (Enter key or button) when both fields are set.
	 */
	const onSubmit = (e) => {
		e.preventDefault()
		if (email && reference && !fetching) {
			checkBookingDetails()
		}
	}

	/**
	 * Successful lookup — redirect through to the booking.
	 */
	if (bookingID) {
		return <Redirect to={`/itinerary/${bookingID}`} />
	}

	return (
		<section className={styles.page}>
			{/* Brand / hero panel (becomes a header band on mobile) */}
			<aside
				className={styles.hero}
				style={{ backgroundImage: `url("${BackgroundImage}")` }}>
				<div className={styles.heroOverlay} />
				<div className={styles.heroContent}>
					<img
						className={styles.heroLogo}
						src={Logo}
						alt="Golf Holidays Direct"
					/>
					<h1>Manage your golf holiday</h1>
					<p>View your itinerary, tee times and payments - all in one place, wherever you are in the world.</p>

					<div className={styles.trust}>
						<ShieldCheck size={18} />
						<span>ATOL protected &middot; Money held in trust</span>
					</div>
				</div>
			</aside>

			{/* Login form panel */}
			<main className={styles.panel}>
				<div className={styles.card}>
					<img
						className={styles.cardLogo}
						src={Logo}
						alt="Golf Holidays Direct"
					/>

					<header className={styles.cardHead}>
						<h2>Access your booking</h2>
						<p>Enter the email address on your booking and your booking reference to continue.</p>
					</header>

					<form
						className={styles.form}
						onSubmit={onSubmit}>
						<label className={styles.field}>
							<span className={styles.label}>Email address</span>
							<div className={styles.inputWrap}>
								<Mail
									className={styles.inputIcon}
									size={18}
								/>
								<input
									type="email"
									autoComplete="email"
									placeholder="you@example.com"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</div>
						</label>

						<label className={styles.field}>
							<span className={styles.label}>Booking reference</span>
							<div className={styles.inputWrap}>
								<Ticket
									className={styles.inputIcon}
									size={18}
								/>
								<input
									type="text"
									inputMode="text"
									placeholder="e.g. 10482"
									value={reference}
									onChange={(e) => setReference(e.target.value)}
								/>
							</div>
						</label>

						{errors.global && <p className={styles.error}>{errors.global}</p>}

						<button
							type="submit"
							className={styles.submit}
							disabled={!email || !reference || fetching}>
							{fetching ? (
								<>
									<span className={styles.spinner} />
									Finding your booking…
								</>
							) : (
								<>
									View my booking
									<ArrowRight size={18} />
								</>
							)}
						</button>
					</form>

					<p className={styles.help}>
						Can&apos;t find your reference? It&apos;s on your confirmation email, or just contact your sales agent.
					</p>
				</div>
			</main>
		</section>
	)
}

export default ClientLogin
